import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Icoon } from "../../public/DierMatchIcoon";
import { useBovenaan } from "../../public/naarBoven";
import {
  EIS_LABELS,
  fetchMatch,
  fetchMatchChips,
  type MatchChip,
  type PublicMatchAnimal,
  type PublicMatchResult,
} from "../../public/publicApi";
import {
  STAPPEN,
  chipLabel,
  optiesVoor,
  stapCompleet,
  titelVan,
  vragenVoor,
  zichtbareAntwoorden,
  zichtbareStappen,
  type Antwoorden,
  type StapId,
  type Vraag,
} from "../../public/match/vragen";
import {
  EERSTE_STAP,
  bewaar,
  lees,
  magNaar,
  metKeuze,
  metVoorstel,
  stapIndex,
  vorigeStap,
  volgendeStap,
  voortgang,
  wis,
} from "../../public/match/matchState";
import {
  fotoVoor,
  useWaaromFotos,
  type WaaromFoto,
} from "../../public/match/waaromFotos";

// De AI-matchwizard van DierMatch (epic 12, stories 12-1 en 12-2), naar de aangeleverde mockups:
// genummerde stappen, keuzekaarten in een raster, en naast elke stap het blok "Waarom vragen we
// dit?" dat uitlegt wat er met het antwoord gebeurt.
//
// Dat uitlegblok is geen opsmuk. De wizard vraagt naar je huishouden, je werkdag en je huisdieren;
// dan hoor je te kunnen lezen waaróm. Het noemt bovendien per stap waar het antwoord tegenaan
// gelegd wordt, en dat is precies wat deze wizard onderscheidt van een formulier dat ergens in een
// zwarte doos verdwijnt.
//
// Toetsenbord: de keuzekaarten zijn echte `input[type=radio|checkbox]` met een label eromheen. Dat
// levert pijltjestoetsen binnen een groep, spatie om aan te zetten en de juiste rol voor een
// schermlezer op, zonder dat er één regel toetsafhandeling in dit bestand staat.
//
// Nog niet hier: de resultatenlijst (12-6), het uitlegpaneel per dier (12-7) en de vrije wens
// (12-8). De samenvatting eindigt daarom met wat we gáán zoeken — geen knop die nog niets doet.

interface Props {
  /** Terug naar het dierenoverzicht. */
  onTerug: () => void;
  /** Naar de bestaande dierpagina — de wizard eindigt daar, en niet in een eigen detail. */
  onDier: (slug: string) => void;
}

export function DierMatchMatch({ onTerug, onDier }: Props) {
  const opgeslagen = useMemo(lees, []);
  const [antwoorden, setAntwoorden] = useState<Antwoorden>(opgeslagen.antwoorden);
  const [stap, setStap] = useState<StapId>(opgeslagen.stap);
  // De intro (S01) staat vóór stap 1 en telt niet mee in de balk. Wie al antwoorden heeft staan
  // krijgt hem ook te zien, maar dan met "verder waar je was" ernaast.
  const [gestart, setGestart] = useState(opgeslagen.lijst);
  // Bij de ouder en niet bij de samenvatting: wie een dier aanklikt en terugkomt hoort zijn tien
  // matches terug te zien, en dat is pas waar als deze stand net zo goed bewaard wordt als de
  // antwoorden zelf.
  const [toonLijst, setToonLijst] = useState(opgeslagen.lijst);
  // De beelden naast de vragen: dieren uit het aanbod, van de soort die in stap 1 gekozen is.
  const waaromFotos = useWaaromFotos(antwoorden.soort);

  useEffect(() => {
    document.title = "Vind jouw perfecte match · DierMatch";
  }, []);

  useEffect(() => {
    bewaar({ antwoorden, stap, lijst: toonLijst });
  }, [antwoorden, stap, toonLijst]);

  // Elke stap begint bovenaan, net als in het kennismakingsformulier dat deze vorm van hier heeft.
  // Zie useBovenaan.
  const bovenkant = useRef<HTMLDivElement | null>(null);
  useBovenaan(bovenkant, stap);

  const kiesAntwoord = useCallback((vraag: Vraag, waarde: string) => {
    setAntwoorden((huidig) => metKeuze(huidig, vraag, waarde));
  }, []);

  const opnieuw = useCallback(() => {
    wis();
    setAntwoorden({});
    setStap(EERSTE_STAP);
    setToonLijst(false);
    setGestart(true);
  }, []);

  const heeftAntwoorden = Object.keys(antwoorden).length > 0;

  if (!gestart) {
    return (
      <Introductie
        hervatten={heeftAntwoorden}
        onStart={() => setGestart(true)}
        onOpnieuw={opnieuw}
        onTerug={onTerug}
      />
    );
  }

  if (stap === "resultaat") {
    return (
      <Samenvatting
        antwoorden={antwoorden}
        onWijzig={(doel) => setStap(doel)}
        onTerug={onTerug}
        onOpnieuw={opnieuw}
        onDier={onDier}
        toonLijst={toonLijst}
        onToonLijst={setToonLijst}
      />
    );
  }

  const vragen = vragenVoor(stap, antwoorden);
  const info = STAPPEN.find((s) => s.id === stap) ?? STAPPEN[0];
  const compleet = stapCompleet(stap, antwoorden);
  const { nummer, totaal } = voortgang(stap, antwoorden);
  const eersteStap = stapIndex(stap, antwoorden) === 0;

  return (
    <div className="dm-match dm-shell" ref={bovenkant}>
      <Stappenbalk stap={stap} antwoorden={antwoorden} onSpring={setStap} />

      <div className="dm-match-lay">
        <div className="dm-match-kaart">
          <h1><span className="dm-match-nr">{nummer}.</span> {info.kop}</h1>
          <p className="dm-match-onder">{info.onder}</p>

          {vragen.map((vraag) => (
            <VraagBlok
              key={vraag.sleutel}
              vraag={vraag}
              antwoorden={antwoorden}
              gekozen={antwoorden[vraag.sleutel] ?? []}
              onKies={(waarde) => kiesAntwoord(vraag, waarde)}
            />
          ))}

          {stap === "voorkeuren" && (
            <VrijeWens antwoorden={antwoorden} onOvernemen={setAntwoorden} />
          )}

          <div className="dm-match-voet">
            <button
              type="button"
              className="dm-match-terug"
              onClick={() => (eersteStap ? setGestart(false) : setStap(vorigeStap(stap, antwoorden)))}
            >
              ← Vorige
            </button>
            <button
              type="button"
              className="dm-match-verder"
              disabled={!compleet}
              onClick={() => setStap(volgendeStap(stap, antwoorden))}
            >
              Volgende <Icoon naam="pijlRechts" size={17} />
            </button>
          </div>
          {!compleet && (
            <p className="dm-match-blokkade" role="status">
              Beantwoord de vragen met een <span aria-hidden="true">*</span> om verder te kunnen —
              daarna weten we welke vragen er nog toe doen.
            </p>
          )}
          <p className="dm-match-tel">Stap {nummer} van {totaal}</p>
        </div>

        {info.waarom && <Waarom tekst={info.waarom} foto={fotoVoor(waaromFotos, nummer - 1)} />}
      </div>
    </div>
  );
}

// ── vrije wens (story 12-8) ─────────────────────────────────────────────────────────────────────

/**
 * "Vertel het in je eigen woorden" — met een tussenstap.
 *
 * <p>Wat je typt verandert niets. De AI stelt voorkeuren vóór, die verschijnen als chips, en pas als
 * je ze overneemt staan ze tussen je antwoorden. Het bouwplan vraagt dat met zoveel woorden, en het
 * is ook de enige vorm waarin een taalmodel hier thuishoort: het mag lezen, niet beslissen.
 *
 * <p>Zonder AI-sleutel op de server antwoordt het endpoint 503 en verdwijnt dit blok. Een tekstvak
 * dat tekst opneemt waar niets mee gebeurt, doet alsof.
 */
function VrijeWens({
  antwoorden,
  onOvernemen,
}: {
  antwoorden: Antwoorden;
  onOvernemen: (volgende: Antwoorden) => void;
}) {
  const [tekst, setTekst] = useState("");
  const [fase, setFase] = useState<"rust" | "bezig" | "klaar" | "fout">("rust");
  const [chips, setChips] = useState<MatchChip[]>([]);
  const [beschikbaar, setBeschikbaar] = useState(true);

  if (!beschikbaar) {
    return null;
  }

  const lees = () => {
    if (!tekst.trim()) {
      return;
    }
    setFase("bezig");
    fetchMatchChips(tekst.trim())
      .then((gevonden) => {
        if (gevonden === null) {
          setBeschikbaar(false);
          return;
        }
        setChips(gevonden);
        setFase("klaar");
      })
      .catch(() => setFase("fout"));
  };

  const neemOver = (chip: MatchChip) => {
    const herkend = chipLabel(chip.sleutel, chip.waarde, antwoorden);
    if (!herkend) {
      return;
    }
    onOvernemen(metVoorstel(antwoorden, herkend.vraag, chip.waarde));
    setChips((huidig) => huidig.filter((c) => c !== chip));
  };

  const leesbaar = chips
    .map((chip) => ({ chip, herkend: chipLabel(chip.sleutel, chip.waarde, antwoorden) }))
    .filter((x) => x.herkend !== null);

  return (
    <div className="dm-match-vraag dm-match-wens">
      <label className="dm-match-vraagtitel" htmlFor="dm-wens">
        Nog iets anders? Vertel het in je eigen woorden
      </label>
      <p className="dm-match-wens-uitleg">
        We zetten je tekst om in voorkeuren die je zelf kunt nakijken. Er verandert niets totdat je
        ze overneemt.
      </p>
      <textarea
        id="dm-wens"
        rows={3}
        value={tekst}
        maxLength={600}
        placeholder="Bijvoorbeeld: ik werk drie dagen buiten de deur en zoek een rustige hond die goed alleen kan zijn."
        onChange={(e) => setTekst(e.target.value)}
      />
      <div className="dm-match-wens-voet">
        <button
          type="button"
          className="dm-match-wensknop"
          onClick={lees}
          disabled={fase === "bezig" || !tekst.trim()}
        >
          {fase === "bezig" ? "Bezig…" : "Zet om in voorkeuren"}
        </button>
        {fase === "fout" && (
          <span className="dm-match-wens-melding" role="status">
            Dat lukte even niet. Je kunt de vragen gewoon zelf invullen.
          </span>
        )}
        {fase === "klaar" && leesbaar.length === 0 && (
          <span className="dm-match-wens-melding" role="status">
            Hier konden we geen voorkeur uit halen — vul het hierboven zelf in.
          </span>
        )}
      </div>

      {leesbaar.length > 0 && (
        <div className="dm-match-chips">
          <p>Klopt dit? Klik aan wat je wilt overnemen.</p>
          <ul>
            {leesbaar.map(({ chip, herkend }) => (
              <li key={`${chip.sleutel}:${chip.waarde}`}>
                <button type="button" onClick={() => neemOver(chip)}>
                  <span className="dm-match-chip-vraag">{titelVan(herkend!.vraag, antwoorden)}</span>
                  <span className="dm-match-chip-waarde">{herkend!.label}</span>
                  <span className="dm-match-chip-plus" aria-hidden="true">+</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Het blok naast de vragen: waarom we dit vragen, en wat er met het antwoord gebeurt.
 *
 * Bovenaan een dier uit het aanbod — de soort die de bezoeker in stap 1 koos. Is er geen foto
 * (nog niet opgehaald, of het ophalen mislukte), dan staat het icoon er zoals voorheen.
 */
function Waarom({ tekst, foto }: { tekst: string; foto: WaaromFoto | null }) {
  return (
    <aside className="dm-match-waarom" aria-labelledby="dm-waarom-kop">
      {foto ? (
        <figure className="dm-match-waarom-beeld">
          <img src={foto.url} alt={foto.alt} loading="lazy" />
          <figcaption>
            {foto.naam}
            {foto.opvangNaam && <span> · {foto.opvangNaam}</span>}
          </figcaption>
        </figure>
      ) : (
        <span className="dm-match-waarom-icoon"><Icoon naam="match" size={24} /></span>
      )}
      <h2 id="dm-waarom-kop">Waarom vragen we dit?</h2>
      {tekst.split("\n\n").map((alinea) => <p key={alinea.slice(0, 24)}>{alinea}</p>)}
    </aside>
  );
}

// ── S01 — introductie ───────────────────────────────────────────────────────────────────────────

function Introductie({
  hervatten,
  onStart,
  onOpnieuw,
  onTerug,
}: {
  hervatten: boolean;
  onStart: () => void;
  onOpnieuw: () => void;
  onTerug: () => void;
}) {
  return (
    <div className="dm-match dm-match--intro dm-shell">
      <button type="button" className="dm-match-terug dm-match-introterug" onClick={onTerug}>
        ← Terug naar zoeken
      </button>

      <div className="dm-match-introkaart">
        <div className="dm-match-introtekst">
          <span className="dm-match-badge">AI match</span>
          <h1>Vind jouw<br />perfecte match</h1>
          <p className="dm-match-onder">
            Beantwoord een paar vragen over je situatie en wensen. Daarna laten we zien welke dieren
            uit het asiel daar het beste bij passen — en waarom.
          </p>

          <ul className="dm-match-voordelen">
            {[
              ["hart", "Persoonlijk en betrouwbaar", "De vragen passen zich aan: een hond krijgt andere vragen dan een konijn."],
              ["schild", "Alleen dieren van aangesloten asielen", "We tonen uitsluitend dieren die nu echt een thuis zoeken."],
              ["match", "Duidelijke uitleg waarom het matcht", "Bij elk dier lees je waarom het past, en wat het asiel nog niet weet."],
              ["huis", "Je gegevens blijven privé", "Geen account, geen naam of adres, en niets wordt op onze server bewaard."],
            ].map(([icoon, titel, tekst]) => (
              <li key={titel}>
                <span className="dm-match-voordeel-icoon"><Icoon naam={icoon} size={19} /></span>
                <div><strong>{titel}</strong><p>{tekst}</p></div>
              </li>
            ))}
          </ul>

          <div className="dm-match-introknoppen">
            <button type="button" className="dm-match-verder" onClick={onStart}>
              {hervatten ? "Verder waar je was" : "Start vragenlijst"} <Icoon naam="pijlRechts" size={17} />
            </button>
            {hervatten && (
              <button type="button" className="dm-match-terug" onClick={onOpnieuw}>
                Opnieuw beginnen
              </button>
            )}
          </div>
        </div>

        <div className="dm-match-introbeeld" aria-hidden="true">
          <img src="/diermatch-hero.jpg" alt="" />
        </div>
      </div>
    </div>
  );
}

// ── stappenbalk ─────────────────────────────────────────────────────────────────────────────────

function Stappenbalk({
  stap,
  antwoorden,
  onSpring,
}: {
  stap: StapId;
  antwoorden: Antwoorden;
  onSpring: (doel: StapId) => void;
}) {
  const route = zichtbareStappen(antwoorden);
  const huidig = stapIndex(stap, antwoorden);
  return (
    <nav className="dm-match-balk" aria-label="Voortgang">
      <ol style={{ gridTemplateColumns: `repeat(${route.length}, 1fr)` }}>
        {route.map((id, i) => {
          const info = STAPPEN.find((s) => s.id === id);
          const gedaan = i < huidig;
          const mag = magNaar(id, stap, antwoorden);
          return (
            <li
              key={id}
              className={`dm-match-stap${i === huidig ? " is-huidig" : ""}${gedaan ? " is-gedaan" : ""}`}
            >
              <button
                type="button"
                onClick={() => onSpring(id)}
                disabled={!mag}
                aria-current={i === huidig ? "step" : undefined}
              >
                <span className="dm-match-bol" aria-hidden="true">{gedaan ? "✓" : i + 1}</span>
                <span className="dm-match-staplabel">{info?.label ?? id}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

// ── één vraag ───────────────────────────────────────────────────────────────────────────────────

function VraagBlok({
  vraag,
  antwoorden,
  gekozen,
  onKies,
}: {
  vraag: Vraag;
  antwoorden: Antwoorden;
  gekozen: string[];
  onKies: (waarde: string) => void;
}) {
  const titelId = `dm-vraag-${vraag.sleutel}`;
  const titel = titelVan(vraag, antwoorden);

  if (vraag.vorm === "tekst") {
    return (
      <div className="dm-match-vraag">
        <label className="dm-match-vraagtitel" htmlFor={titelId}>{titel}</label>
        <div className="dm-match-tekstveld">
          <Icoon naam="speld" size={17} />
          <input
            id={titelId}
            value={gekozen[0] ?? ""}
            placeholder={vraag.plaatshouder}
            onChange={(e) => onKies(e.target.value)}
          />
        </div>
      </div>
    );
  }

  const opties = optiesVoor(vraag, antwoorden);
  const enkel = vraag.vorm === "enkel";
  // Tegels met een icoon en zonder bijzin staan in de mockup gestapeld: icoon boven, label eronder.
  // Dat leest compacter, en bij zes of zeven keuzes past de rij dan nog op één regel.
  const gestapeld = opties.length >= 4 && opties.every((o) => o.icoon && !o.hint);

  return (
    <fieldset className="dm-match-vraag">
      <legend className="dm-match-vraagtitel" id={titelId}>
        {titel}
        {vraag.verplicht && <span className="dm-match-verplicht" aria-label="verplicht"> *</span>}
      </legend>
      <div
        className={gestapeld ? "dm-match-opties dm-match-opties--tegels" : "dm-match-opties"}
        role="group"
        aria-labelledby={titelId}
      >
        {opties.map((optie) => {
          const aan = gekozen.includes(optie.waarde);
          return (
            <label
              key={optie.waarde || "leeg"}
              className={aan ? "dm-match-optie is-aan" : "dm-match-optie"}
            >
              <input
                type={enkel ? "radio" : "checkbox"}
                name={vraag.sleutel}
                value={optie.waarde}
                checked={aan}
                onChange={() => onKies(optie.waarde)}
                // Een radio die je nog eens aanklikt laat los (zie `kies`); zonder dit vangt de
                // browser de tweede klik af omdat hij al aan staat.
                onClick={enkel && aan ? () => onKies(optie.waarde) : undefined}
              />
              {optie.icoon && (
                <span className="dm-match-optie-icoon"><Icoon naam={optie.icoon} size={22} /></span>
              )}
              <span className="dm-match-optie-tekst">
                <strong>{optie.label}</strong>
                {optie.hint && <small>{optie.hint}</small>}
              </span>
              <span className="dm-match-vink" aria-hidden="true">✓</span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

// ── S07 — samenvatting ──────────────────────────────────────────────────────────────────────────

function Samenvatting({
  antwoorden,
  onWijzig,
  onTerug,
  onOpnieuw,
  onDier,
  toonLijst,
  onToonLijst,
}: {
  antwoorden: Antwoorden;
  onWijzig: (doel: StapId) => void;
  onTerug: () => void;
  onOpnieuw: () => void;
  onDier: (slug: string) => void;
  toonLijst: boolean;
  onToonLijst: (aan: boolean) => void;
}) {
  // Alleen wat nog zichtbaar is telt: wie terugliep en van hond naar kat wisselde, heeft antwoorden
  // op vragen staan die niet meer bestaan. Die horen niet in de samenvatting en niet in de
  // zoekopdracht.
  const schoon = useMemo(() => zichtbareAntwoorden(antwoorden), [antwoorden]);
  const route = zichtbareStappen(antwoorden).filter((s) => s !== "resultaat");

  const [uitslag, setUitslag] = useState<PublicMatchResult | null>(null);
  const [fase, setFase] = useState<"laden" | "klaar" | "fout">("laden");
  /** Ophogen vraagt het opnieuw; dat is wat "Opnieuw proberen" doet zonder de antwoorden te raken. */
  const [poging, setPoging] = useState(0);

  useEffect(() => {
    let levend = true;
    setFase("laden");
    fetchMatch(schoon)
      .then((resultaat) => {
        if (levend) {
          setUitslag(resultaat);
          setFase("klaar");
        }
      })
      .catch(() => {
        if (levend) {
          setFase("fout");
        }
      });
    return () => {
      levend = false;
    };
  }, [schoon, poging]);

  if (toonLijst && uitslag) {
    return (
      <Resultatenlijst
        uitslag={uitslag}
        onTerug={() => onToonLijst(false)}
        onDier={onDier}
      />
    );
  }

  // Terugkomen van een dierpagina haalt de lijst opnieuw op — het aanbod kan ondertussen gewijzigd
  // zijn. Daarbij hoort een kort bericht, niet de hele samenvatting die een tel later weer weg is.
  if (toonLijst && fase !== "klaar") {
    return (
      <div className="dm-match dm-shell">
        <div className="dm-match-kop">
          <h1>{fase === "fout" ? "Je matches zijn even niet op te halen" : "Je matches worden opgehaald…"}</h1>
          <p className="dm-match-onder" role="status">
            {fase === "fout"
              ? "Je antwoorden staan nog. Probeer het zo nog eens."
              : "We leggen je antwoorden opnieuw naast het actuele aanbod."}
          </p>
          <button type="button" className="dm-match-terug" onClick={() => onToonLijst(false)}>
            ← Terug naar je antwoorden
          </button>
        </div>
      </div>
    );
  }

  const aantal = uitslag?.resultaten.length ?? 0;

  return (
    <div className="dm-match dm-shell">
      <Stappenbalk stap="resultaat" antwoorden={antwoorden} onSpring={onWijzig} />

      <div className="dm-match-lay dm-match-lay--breed">
        <div className="dm-match-kaart">
          {fase === "laden" && (
            <>
              <h1>We zoeken je matches bij elkaar…</h1>
              <p className="dm-match-onder" role="status">
                We leggen je antwoorden naast de dieren die nu bij de aangesloten asielen wachten.
              </p>
            </>
          )}

          {fase === "fout" && (
            <>
              <h1>Dat lukte niet</h1>
              <p className="dm-match-onder">
                We konden de matches even niet ophalen. Je antwoorden staan nog; probeer het zo
                nog eens, of zoek zelf verder in het aanbod.
              </p>
            </>
          )}

          {fase === "klaar" && aantal > 0 && (
            <>
              <span className="dm-match-eyebrow">Klaar</span>
              <h1>We hebben jouw match gevonden!</h1>
              <p className="dm-match-onder">
                Op basis van je antwoorden {aantal === 1 ? "past er één dier" : `passen er ${aantal} dieren`}
                {" "}uit {uitslag!.totaal} beschikbare dieren het beste bij je.
              </p>
            </>
          )}

          {fase === "klaar" && aantal === 0 && (
            <Geenmatches uitslag={uitslag!} onWijzig={() => onWijzig("voorkeuren")} />
          )}

          {/* Ook mét matches: de cijfers laten zien wat jouw situatie betekent voor de dieren
              die nu wachten, en dat is precies waar deze wizard over gaat. */}
          {fase === "klaar" && aantal > 0 && (
            <Afvallers uitslag={uitslag!} onWijzig={() => onWijzig("voorkeuren")} />
          )}

          <dl className="dm-match-samenvatting">
            {route.map((id) => {
              const info = STAPPEN.find((s) => s.id === id);
              const regels = vragenVoor(id, antwoorden)
                .map((vraag) => ({ vraag, waarden: schoon[vraag.sleutel] ?? [] }))
                .filter((r) => r.waarden.length > 0);
              if (regels.length === 0) {
                return null;
              }
              return (
                <div className="dm-match-samenvatting-blok" key={id}>
                  <dt>
                    {info?.label ?? id}
                    <button type="button" onClick={() => onWijzig(id)}>wijzigen</button>
                  </dt>
                  <dd>
                    {regels.map(({ vraag, waarden }) => (
                      <p key={vraag.sleutel}>
                        <span>{titelVan(vraag, antwoorden)}</span>
                        <b>{waarden.map((w) => labelVoor(vraag, antwoorden, w)).join(", ")}</b>
                      </p>
                    ))}
                  </dd>
                </div>
              );
            })}
          </dl>

          {/* Eén hoofdknop, en die leidt nooit wég van je matches. Hij stond hier eerder op
              "Bekijk alle dieren" zolang het rangschikken liep: je zag een ingevulde samenvatting
              met een knop die eruitzag als de volgende stap, klikte, en belandde op de homepagina. */}
          <div className="dm-match-introknoppen">
            {fase === "laden" && (
              <button type="button" className="dm-match-verder" disabled>
                Matches zoeken…
              </button>
            )}
            {fase === "fout" && (
              <button type="button" className="dm-match-verder" onClick={() => setPoging((p) => p + 1)}>
                Opnieuw proberen <Icoon naam="pijlRechts" size={17} />
              </button>
            )}
            {fase === "klaar" && aantal > 0 && (
              <button type="button" className="dm-match-verder" onClick={() => onToonLijst(true)}>
                Bekijk jouw {aantal} {aantal === 1 ? "match" : "matches"} <Icoon naam="pijlRechts" size={17} />
              </button>
            )}
            {fase === "klaar" && aantal === 0 && (
              <button type="button" className="dm-match-verder" onClick={onTerug}>
                Bekijk alle dieren <Icoon naam="pijlRechts" size={17} />
              </button>
            )}
            <button type="button" className="dm-match-terug" onClick={onOpnieuw}>
              Opnieuw beginnen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Nul resultaten — met de herstelroute, niet met een schouderophalen.
 *
 * <p>De engine telt per harde eis hoeveel dieren erop afvielen. Daarmee kan hier staan wélke eis
 * het aanbod leegtrok, en kan de bezoeker precies díe eis losser zetten in plaats van alles tegelijk
 * te versoepelen. Veiligheids- en compatibiliteitseisen worden nooit stilzwijgend losgelaten: hij
 * gaat zelf terug naar de vraag.
 */
function Geenmatches({ uitslag, onWijzig }: { uitslag: PublicMatchResult; onWijzig: () => void }) {
  return (
    <>
      <h1>Geen dier dat aan alles voldoet</h1>
      <p className="dm-match-onder">
        Er staan op dit moment geen dieren op DierMatch die aan al je eisen voldoen. Dat zegt niets
        over jouw wensen — het aanbod wisselt dagelijks.
      </p>
      <Afvallers uitslag={uitslag} onWijzig={onWijzig} />
    </>
  );
}

/**
 * Waar de dieren op afvielen — ook als er wél matches zijn.
 *
 * <p>Stond eerst alleen bij nul resultaten, als herstelroute. Maar deze cijfers doen meer dan een
 * lege lijst verklaren: ze laten zien wat jouw situatie betekent voor de dieren die nu wachten.
 * "62 dieren vielen af op kan bij kinderen" is geen verwijt en geen storing; het is het aanbod dat
 * terugpraat. Daarom staat het er altijd, met tien matches net zo goed als met nul.
 *
 * <p>Drie regels en niet meer. De vierde eis is zelden nog een verrassing, en een lijst die
 * doorloopt leest als een foutmelding in plaats van als een inzicht.
 */
function Afvallers({ uitslag, onWijzig }: { uitslag: PublicMatchResult; onWijzig: () => void }) {
  const zwaarste = Object.entries(uitslag.afgevallen)
    .filter(([eis]) => eis !== "soort" && eis !== "beschikbaarheid")
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);

  if (zwaarste.length === 0) {
    return null;
  }

  return (
    <div className="dm-match-blokkade">
      <p style={{ margin: "0 0 8px" }}>Waar de meeste dieren op afvielen:</p>
      <ul className="dm-match-afval">
        {zwaarste.map(([eis, aantal]) => (
          <li key={eis}>
            <b>{aantal}</b> {aantal === 1 ? "dier viel af" : "dieren vielen af"} op{" "}
            {EIS_LABELS[eis] ?? eis}
          </li>
        ))}
      </ul>
      <button type="button" className="dm-match-terug" onClick={onWijzig} style={{ paddingLeft: 0 }}>
        Pas je eisen aan →
      </button>
    </div>
  );
}

// ── S08 — de top 10 ─────────────────────────────────────────────────────────────────────────────

function Resultatenlijst({
  uitslag,
  onTerug,
  onDier,
}: {
  uitslag: PublicMatchResult;
  onTerug: () => void;
  onDier: (slug: string) => void;
}) {
  const aantal = uitslag.resultaten.length;
  return (
    <div className="dm-match dm-shell">
      {/* De weg terug staat naast de kop en niet als klein linkje erboven: hier zijn twee
          dingen te doen — de dieren bekijken, of nog eens naar je antwoorden kijken — en het
          tweede hoort er niet uit te zien als een ongelukje. */}
      <div className="dm-match-kop dm-match-kop--met-knop">
        <div>
          <h1>
            {aantal === 1 ? "Jouw match" : `Jouw top ${aantal} matches`}
          </h1>
          <p className="dm-match-onder">
            Deze dieren passen het beste bij jouw situatie en wensen. Bij elk dier staat waarom —
            en wat het asiel nog niet heeft ingevuld.
          </p>
        </div>
        <button type="button" className="dm-match-antwoorden" onClick={onTerug}>
          Antwoorden bekijken
        </button>
      </div>

      <ol className="dm-match-lijst">
        {uitslag.resultaten.map((dier) => (
          <MatchKaart key={dier.platformId} dier={dier} onDier={onDier} />
        ))}
      </ol>

      {uitslag.totaal > aantal && (
        <p className="dm-match-meta">
          We lieten de {aantal} best passende zien van {uitslag.totaal} dieren die aan je harde
          eisen voldoen.
        </p>
      )}
    </div>
  );
}

function MatchKaart({ dier, onDier }: { dier: PublicMatchAnimal; onDier: (slug: string) => void }) {
  const [open, setOpen] = useState(false);
  const meta = [dier.soort, dier.leeftijd, dier.geslacht].filter(Boolean).join(" · ");
  const paneelId = `dm-waarom-${dier.platformId}`;

  return (
    <li className="dm-match-kaartje">
      <div className="dm-match-kaartje-foto">
        {dier.hoofdfotoUrl
          ? <img src={dier.hoofdfotoUrl} alt={`Foto van ${dier.naam}`} loading="lazy" />
          : <span className="dm-photo-placeholder" aria-hidden="true"><Icoon naam="poot" size={30} /></span>}
        <span className={`dm-match-band dm-match-band--${dier.band}`}>{dier.bandLabel}</span>
      </div>

      <div className="dm-match-kaartje-tekst">
        <h2>{dier.naam}</h2>
        {meta && <p className="dm-match-kaartje-meta">{meta}</p>}
        {(dier.opvangNaam || dier.plaats) && (
          <p className="dm-match-kaartje-opvang">
            <Icoon naam="speld" size={13} /> {[dier.opvangNaam, dier.plaats].filter(Boolean).join(", ")}
          </p>
        )}

        <ul className="dm-match-redenen">
          {dier.redenen.slice(0, 2).map((reden) => (
            <li key={reden}><span className="dm-match-vink-klein" aria-hidden="true">✓</span>{reden}</li>
          ))}
          {dier.aandachtspunten.slice(0, 1).map((punt) => (
            <li className="is-let-op" key={punt}>
              <span className="dm-match-vink-klein" aria-hidden="true">!</span>{punt}
            </li>
          ))}
        </ul>

        {(dier.redenen.length > 2 || dier.aandachtspunten.length > 1 || dier.onbekend.length > 0) && (
          <>
            <button
              type="button"
              className="dm-match-meer"
              aria-expanded={open}
              aria-controls={paneelId}
              onClick={() => setOpen((o) => !o)}
            >
              {open ? "Minder uitleg" : `Waarom past ${dier.naam} bij jou?`}
            </button>
            {open && (
              <div className="dm-match-uitleg" id={paneelId}>
                {dier.redenen.length > 0 && (
                  <>
                    <h3>Wat past</h3>
                    <ul>{dier.redenen.map((r) => <li key={r}>{r}</li>)}</ul>
                  </>
                )}
                {dier.aandachtspunten.length > 0 && (
                  <>
                    <h3>Waar je op moet letten</h3>
                    <ul>{dier.aandachtspunten.map((r) => <li key={r}>{r}</li>)}</ul>
                  </>
                )}
                {dier.onbekend.length > 0 && (
                  <>
                    <h3>Nog niet bekend</h3>
                    <ul>{dier.onbekend.map((r) => <li key={r}>{r}</li>)}</ul>
                    <p className="dm-match-uitleg-voet">
                      Dit heeft het asiel nog niet ingevuld, dus het telde niet mee. Vraag het
                      gerust bij een kennismaking.
                    </p>
                  </>
                )}
              </div>
            )}
          </>
        )}

        <button type="button" className="dm-match-verder dm-match-bekijk" onClick={() => onDier(dier.slug)}>
          Bekijk {dier.naam}
        </button>
      </div>
    </li>
  );
}

/** Het leesbare label bij een gekozen waarde; een tekstantwoord is zijn eigen label. */
function labelVoor(vraag: Vraag, antwoorden: Antwoorden, waarde: string): string {
  if (vraag.vorm === "tekst") {
    return waarde;
  }
  return optiesVoor(vraag, antwoorden).find((o) => o.waarde === waarde)?.label ?? waarde;
}
